import { COUNTDOWN_TARGET } from '@/config/eclipse'
import { useCountdown } from '@/hooks/useCountdown'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/cn'

/**
 * The same remaining time expressed on the calendar: months, weeks, and the
 * leftover days. A second point of view on the headline day count — useful for
 * programme planning, where "9 months" reads more concretely than "289 days".
 */
export function CalendarView({ className }: { className?: string }) {
  const { t } = useI18n()
  const { months, weeks, days } = useCountdown(COUNTDOWN_TARGET)

  const parts = [
    { key: 'months', value: months, label: t.countdown.months },
    { key: 'weeks', value: weeks, label: t.countdown.weeks },
    { key: 'days', value: days, label: t.countdown.days },
  ]

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <p className="text-[0.625rem] uppercase tracking-[0.24em] text-faint">
        {t.countdown.calendarViewLabel}
      </p>

      <dl className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-2 sm:gap-x-3">
        {parts.map((part, index) => (
          <div key={part.key} className="flex items-baseline gap-2">
            <dt className="sr-only">{part.label}</dt>
            <dd className="flex items-baseline gap-1.5">
              <span className="tabular font-serif text-2xl text-parchment sm:text-3xl">
                {part.value}
              </span>
              <span className="text-xs uppercase tracking-[0.14em] text-muted sm:text-sm">
                {part.label}
              </span>
            </dd>
            {index < parts.length - 1 ? (
              <span aria-hidden="true" className="px-1 text-gold/45">
                ·
              </span>
            ) : null}
          </div>
        ))}
      </dl>
    </div>
  )
}
